/**
 * Sorts an array of objects based on multiple sort descriptors using natural sorting.
 * Filters out invalid sort descriptors and sorts the data accordingly.
 * @param data - The array of objects to be sorted. (left unmutated)
 * @param sortDescriptors - An array of sort descriptors containing field and direction.
 * @returns A new array of sorted objects.
 */
export function naturalOrderBy(data: any[], sortDescriptors: { field: string, dir?: string; }[]): any[] {
    if (!sortDescriptors || sortDescriptors.length === 0) {
        return data;
    }

    const validSortDescriptors = sortDescriptors.filter(descriptor => descriptor.field && descriptor.dir);
    if (validSortDescriptors.length === 0) {
        return data;
    }

    const sorted = [...data].sort((a, b) => {
        for (const { field, dir } of validSortDescriptors) {
            const aValue = a[field];
            const bValue = b[field];

            const normalizedField = field.toLocaleLowerCase();
            const compareResults = normalizedField == "siteno" ?
                splitNaturalComparer(aValue, bValue, dir) :
                naturalComparer(aValue, bValue, dir);
            if (compareResults !== 0) {
                return compareResults;
            }
        }
        return 0;
    });
    return sorted;
}

/**
 * Compares two string values for sorting purposes using natural sorting.
 * Splits the strings into parts and compares each part using naturalComparer.
 * Returns a number indicating the sort order.
 * @param aValue - The first value to compare.
 * @param bValue - The second value to compare.
 * @param order - The direction for sorting ('asc' for ascending, 'desc' for descending).
 * @returns A number indicating the sort order.
 */

export function splitNaturalComparer(aValue: string, bValue: string, order = 'asc'): number {
    const aParts = splitString(aValue);
    const bParts = splitString(bValue);

    if (aParts.length === 1 && bParts.length === 1) {
        return naturalComparer(aValue, bValue, order);
    }

    const part1Result = naturalComparer(aParts[0], bParts[0], order);

    return part1Result === 0 ? naturalComparer(aParts[1], bParts[1], order) : part1Result;
}

/**
 * Compares two string values for sorting purposes.
 * Handles the following conditions: null, number, date, and string.
 * Returns a number indicating the sort order.
 * @param aValue - The first value to compare.
 * @param bValue - The second value to compare.
 * @param order - The direction multiplier for sorting (asc for ascending, desc for descending).
 * @returns A number indicating the sort order.
 */

export function naturalComparer(aValue: string | null, bValue: string | null, order = 'asc'): number {
    const dirMultiplier = order === 'asc' ? 1 : -1;

    aValue = aValue ?? '';
    bValue = bValue ?? '';

    if (typeof aValue === "string") {
        aValue = aValue.trim().toLocaleLowerCase();
    }

    if (typeof bValue === "string") {
        bValue = bValue.trim().toLocaleLowerCase();
    }

    if (aValue === bValue) {
        return 0;
    }

    if (aValue === '') {
        return -1 * dirMultiplier;
    }

    if (bValue === '') {
        return 1 * dirMultiplier;
    }

    // Parsed once per value (never per pair, and never twice for the same value), so the
    // same two values always compare the same way regardless of what else they're being
    // compared against - required for a transitive sort.
    const a = toComparable(aValue);
    const b = toComparable(bValue);

    if (a.type !== b.type) {
        return (a.type < b.type ? -1 : 1) * dirMultiplier;
    }

    if (a.type === ComparisonType.Numeric && b.type === ComparisonType.Numeric) {
        if (a.value < b.value) {
            return -1 * dirMultiplier;
        }

        if (a.value > b.value) {
            return 1 * dirMultiplier;
        }

        return 0;
    }

    if (a.type === ComparisonType.Date && b.type === ComparisonType.Date) {
        if (a.value < b.value) {
            return -1 * dirMultiplier;
        }

        if (a.value > b.value) {
            return 1 * dirMultiplier;
        }

        return 0;
    }

    if (a.type === ComparisonType.Text && b.type === ComparisonType.Text) {
        if (a.value < b.value) {
            return -1 * dirMultiplier;
        }

        if (a.value > b.value) {
            return 1 * dirMultiplier;
        }
    }
    return 0;
}

const ComparisonType = {
    Numeric: 0,
    Date: 1,
    Text: 2,
} as const;

function toComparable(value: string):
    | { type: typeof ComparisonType.Date; value: Date; }
    | { type: typeof ComparisonType.Numeric; value: number; }
    | { type: typeof ComparisonType.Text; value: string; } {
    const num = Number(value);
    if (!isNaN(num)) {
        return { type: ComparisonType.Numeric, value: num };
    }

    const date = new Date(value);
    if (!isNaN(date.getTime())) {
        return { type: ComparisonType.Date, value: date };
    }

    return { type: ComparisonType.Text, value };
}

/*
 * Splits the input string into two parts based on predefined regex patterns.
 * Matches the following patterns in the following order of priority {alphanumeric}-{alphanumeric}, {non-numeric}{numeric}, {numeric}{non-numeric}
 * Returns an array with the matched parts or the full string and null if no pattern matches.
 * @param input - The string to be split.
 * @returns An array with two parts of the split string.
 */
function splitString(input: string | null): (string | null)[] {
    if (!input?.match) {
        return [null, null];
    }

    const dateRegex = /^(0[1-9]|1[0-2])[\/-](0[1-9]|[12]\d|3[01])[\/-](19|20)\d{2}$/;
    const numericNonNumericRegex = /^(\d+)(\D+)$/;
    const nonNumericNumericRegex = /^(\D+)(\d+)$/;
    const alphanumericHyphenAlphanumericRegex = /^([\w]+)-([\w]+)$/;

    if (input.match(dateRegex)) {
        return [input, null];
    }

    if (!isNaN(Number(input))) {
        return [input, null];
    }

    let match: RegExpMatchArray | null;

    // Note: strings may match more than one regex, the order below sets the priority
    match = input.match(alphanumericHyphenAlphanumericRegex);
    if (match) {
        return [match[1], match[2]];
    }

    match = input.match(numericNonNumericRegex);
    if (match) {
        return [match[1], match[2]];
    }

    match = input.match(nonNumericNumericRegex);
    if (match) {
        return [match[1], match[2]];
    }

    return [input, null];
}
